import type {
	JSONSchemaString,
	UISchema,
} from '@/shared/tools/json-schema-utils'
import type { JSONSchemaType } from 'ajv'

import React, { Fragment } from 'react'
import { entries, keys, last, prop, split } from 'lodash/fp'

import { getEnumOptions } from '@/shared/tools/json-schema-utils'

import {
	RHFCheckbox,
	RHFCreatableSelect,
	RHFMultiSelect,
	RHFMultiSelectSegmented,
	RHFSelect,
	RHFTextField,
} from '../HookForm'

type AnyJSONScheme = JSONSchemaType<any>
type HookSchemaFormProps = {
	schema: AnyJSONScheme
	name: string
	uiSchema?: UISchema

	errorSchema?: any
}
type SchemaFieldProps = {
	schema: AnyJSONScheme
	name: string
	required?: string[]
	uiSchema?: UISchema

	errorSchema?: any
}
function getFieldLabel(name: string, schema: AnyJSONScheme) {
	return schema.title || last(split('.', name))
}
function getOrderedObjectFields(
	object: Record<string, AnyJSONScheme>,
	order?: string[]
): [string, AnyJSONScheme][] {
	return order?.length === keys(object)?.length
		? order.map((name) => [name, object?.[name]])
		: entries(object)
}
export function SchemaField({
	required,
	schema: _schema,
	name: _name,
	uiSchema: _uiSchema,
	errorSchema,
}: SchemaFieldProps) {
	const uiSchema = prop(_name, _uiSchema)

	const defaultValue = _schema?.default
	const label = getFieldLabel(_name, _schema)
	const hint = _schema.description
	const innerName = last(split('.', _name))
	const error = prop(innerName!, errorSchema)
	const isRequired = required?.includes(innerName as string)
	const commonProps = {
		name: _name,
		label,
		hint,
		defaultValue,
		required: isRequired,
		invalid: !!error,
	}
	const getFieldDataChoices = () =>
		uiSchema?.['ui:options'] ||
		(_schema?.enum ? getEnumOptions(_schema as JSONSchemaString) : []) ||
		[]
	const hasFieldDataChoices = () => uiSchema?.['ui:options'] || _schema?.enum

	switch (_schema?.type) {
		case 'object': {
			const fields = getOrderedObjectFields(
				_schema.properties,
				uiSchema?.['ui:order']
			)

			return (
				<Fragment key={_name}>
					{fields?.map(([name, schema]) => (
						<SchemaField
							required={_schema.required || []}
							key={name}
							schema={schema}
							name={`${_name}.${name}`}
							uiSchema={_uiSchema}
							errorSchema={errorSchema}
						/>
					))}
				</Fragment>
			)
		}
		case 'boolean':
			return <RHFCheckbox {...commonProps} />
		case 'number':
		case 'integer':
			return <RHFTextField {...commonProps} type='number' />
		case 'string': {
			if (hasFieldDataChoices()) {
				return <RHFSelect {...commonProps} options={getFieldDataChoices()} />
			}

			return <RHFTextField {...commonProps} />
		}
		case 'array': {
			// FIXME: ui:field should be changed to MultiSelectSegmented component ob BE side
			if (uiSchema && uiSchema['ui:field'] === 'MultiSelectField') {
				return (
					<RHFMultiSelectSegmented
						{...commonProps}
						options={getFieldDataChoices()}
					/>
				)
			}

			if (hasFieldDataChoices()) {
				return (
					<RHFMultiSelect {...commonProps} options={getFieldDataChoices()} />
				)
			} else {
				return <RHFCreatableSelect {...commonProps} />
			}
		}
		default: {
			if (hasFieldDataChoices()) {
				return <RHFSelect {...commonProps} options={getFieldDataChoices()} />
			} else {
				return <>{JSON.stringify(_schema, null, 2)}</>
			}
		}
	}
}

export default function HookSchemaForm({
	schema,
	name,
	uiSchema,
	errorSchema,
}: HookSchemaFormProps) {
	return (
		<SchemaField
			errorSchema={errorSchema}
			schema={schema}
			name={name}
			uiSchema={uiSchema}
		/>
	)
}
